import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import { DataGrid, GridColumns } from "@mui/x-data-grid";
import { useApiUrl } from "@refinedev/core";
import { List, useDataGrid } from "@refinedev/mui";
import { OrcaDeployLifecycle } from "components/orcaDeployLifecycle";
import React, { useMemo, useState } from "react";
import { dataProvider } from "rest-data-provider";
import {
  fetchLatestOrcaDeployActivityLogRow,
  formatNyTimestamp,
  isBareDomain,
  OrcaDeployActivityLogRow,
  OrcaDeployState,
  OrcaDeployStateLabels,
  OrcaServerSlot,
  ServerSlotKey,
  slotLabel,
} from "rest-data-provider/utils/orcaDeployActivityLogsTypes";
import {
  deriveResult,
  editSlotsAndLinks,
  stage,
} from "rest-data-provider/utils/orcaDeployActivityStateMachine";

const getCurrentOperatorName = (): string => {
  try {
    const userData = JSON.parse(
      window.sessionStorage.getItem("user") as string
    );
    return userData?.nickname || userData?.email || userData?.id || "";
  } catch {
    return "";
  }
};

const SLOT_KEYS: ServerSlotKey[] = ["server1", "server2", "server3", "server4"];

const emptyRow: OrcaDeployActivityLogRow = {
  id: "",
  result: [],
  server1: null,
  server2: null,
  server3: null,
  server4: null,
  prod: "server1",
  second: "server2",
  state: OrcaDeployState.STAGE,
  prodLinkName: "",
  prodLinkDescription: "",
  fallbackLinkName: "",
  fallbackDescription: "",
  betaLinkName: "",
  betaLinkDescription: "",
  activityName: "",
  activityOperatorName: "",
  comment: null,
  createdAt: 0,
};

type DialogMode = "stage" | "edit" | "createNew" | null;

export const OrcaDeployActivityLogsList = () => {
  const apiUrl = useApiUrl();

  const { dataGridProps } = useDataGrid({
    resource: "orca-deploy-activity-logs",
    sorters: {
      initial: [{ field: "createdAt", order: "desc" }],
    },
    pagination: { pageSize: 10 },
  });

  // dataGridProps.rows is already sorted by createdAt desc (page 1, row 0 is
  // the latest), so derive `latest` from it instead of firing a second
  // $limit=1 request for the same row.
  const latest: OrcaDeployActivityLogRow = useMemo(
    () => (dataGridProps.rows[0] as OrcaDeployActivityLogRow) ?? emptyRow,
    [dataGridProps.rows]
  );
  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [submitting, setSubmitting] = useState(false);

  // Whether any record exists yet at all — Create New only makes sense as a
  // bootstrap step before the very first row; once a row exists, Edit covers
  // every field Create New could set (slot url/hash/version, link names),
  // so offering both invites picking the wrong one and losing history.
  const hasAnyRecord = !!dataGridProps.rows.length;

  // Edit form state — one url/hash/version field set per slot, all editable
  // at once (this is also the only way to put a url on server3/server4),
  // plus the 3 link name/description pairs. Fields are pre-filled from
  // `latest` when the dialog opens so an edit can just tweak some values
  // and leave the rest as-is.
  const emptySlotForm: OrcaServerSlot = { url: "", hash: "", version: "" };
  const [editSlots, setEditSlots] = useState<Record<ServerSlotKey, OrcaServerSlot>>({
    server1: emptySlotForm,
    server2: emptySlotForm,
    server3: emptySlotForm,
    server4: emptySlotForm,
  });
  // Tracks which slots the user actually edited in this dialog session. On
  // submit, only touched slots are written from the form — untouched slots
  // fall back to the freshly re-fetched latest row's value, so a stale
  // dialog-open-time snapshot of e.g. server3 can't clobber a concurrent
  // edit someone else made to it while this dialog was open.
  const [editedSlotKeys, setEditedSlotKeys] = useState<Set<ServerSlotKey>>(new Set());
  // Which slot second points at. Prod has no manual-edit case — it only
  // ever changes via Deploy/Rollback swapping the pointer — so there is no
  // editProd; only second is user-editable, and only while state is Stage.
  // Stable/Unstable mean second is either live or held as a fallback, so
  // repointing it there would silently swap what's currently serving users
  // instead of going through Deploy/Rollback.
  const [editSecond, setEditSecond] = useState<ServerSlotKey>("server2");
  const [editProdLinkName, setEditProdLinkName] = useState("");
  const [editProdLinkDescription, setEditProdLinkDescription] = useState("");
  const [editFallbackLinkName, setEditFallbackLinkName] = useState("");
  const [editFallbackDescription, setEditFallbackDescription] = useState("");
  const [editBetaLinkName, setEditBetaLinkName] = useState("");
  const [editBetaDescription, setEditBetaDescription] = useState("");
  // Note attached to the row this edit appends. Deliberately not pre-filled
  // from `latest` — it describes this one edit, not the previous activity.
  const [editComment, setEditComment] = useState("");

  const setEditSlotField = (
    key: ServerSlotKey,
    field: keyof OrcaServerSlot,
    value: string
  ) => {
    setEditSlots((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
    setEditedSlotKeys((prev) => new Set(prev).add(key));
  };

  const openEditDialog = () => {
    setEditSlots({
      server1: latest.server1 ?? emptySlotForm,
      server2: latest.server2 ?? emptySlotForm,
      server3: latest.server3 ?? emptySlotForm,
      server4: latest.server4 ?? emptySlotForm,
    });
    setEditedSlotKeys(new Set());
    setEditSecond(latest.second);
    setEditProdLinkName(latest.prodLinkName);
    setEditProdLinkDescription(latest.prodLinkDescription);
    setEditFallbackLinkName(latest.fallbackLinkName);
    setEditFallbackDescription(latest.fallbackDescription);
    setEditBetaLinkName(latest.betaLinkName);
    setEditBetaDescription(latest.betaLinkDescription);
    setEditComment("");
    setDialogMode("edit");
  };

  // Create New form state — fully manual, does not derive from latest.
  // Hash/version are optional: a fresh record can be created before either
  // server has a known build, and filled in later via Add Hash.
  const [createForm, setCreateForm] = useState({
    server1Url: "",
    server1Hash: "",
    server1Version: "",
    server2Url: "",
    server2Hash: "",
    server2Version: "",
    prod: "server1" as ServerSlotKey,
    second: "server2" as ServerSlotKey,
    prodLinkName: "Sim",
    prodLinkDescription: "Click to navigate to simulate platform",
    fallbackLinkName: "Sim Old Version",
    fallbackDescription: "Click to navigate to old version simulate platform",
    betaLinkName: "Sim Beta",
    betaLinkDescription: "Click to navigate to beta version simulate platform",
  });

  const columns = React.useMemo<GridColumns<any>>(
    () => [
      {
        field: "createdAt",
        headerName: "Timestamp",
        minWidth: 200,
        hideable: false,
        renderCell: ({ row }) => formatNyTimestamp(row.createdAt),
      },
      {
        field: "activityName",
        headerName: "Activity",
        minWidth: 160,
        hideable: false,
      },
      {
        field: "state",
        headerName: "State",
        minWidth: 130,
        hideable: false,
        renderCell: ({ row }) => OrcaDeployStateLabels[row.state as 0 | 1 | 2],
      },
      {
        field: "activityOperatorName",
        headerName: "Operator",
        minWidth: 150,
        hideable: false,
      },
      {
        field: "prod",
        headerName: "Prod Domain",
        minWidth: 220,
        flex: 1,
        renderCell: ({ row }) => {
          const slot = row[row.prod as ServerSlotKey];
          return slot ? slot.url : "—";
        },
      },
      {
        field: "prodVersion",
        headerName: "Prod Version",
        minWidth: 120,
        renderCell: ({ row }) => {
          const slot = row[row.prod as ServerSlotKey];
          return slot?.version || "—";
        },
      },
      {
        field: "prodHash",
        headerName: "Prod Hash",
        minWidth: 120,
        renderCell: ({ row }) => {
          const slot = row[row.prod as ServerSlotKey];
          return slot?.hash || "—";
        },
      },
      {
        field: "second",
        headerName: "Second Domain",
        minWidth: 220,
        flex: 1,
        renderCell: ({ row }) => {
          const slot = row[row.second as ServerSlotKey];
          return slot ? slot.url : "—";
        },
      },
      {
        field: "secondVersion",
        headerName: "Second Version",
        minWidth: 130,
        renderCell: ({ row }) => {
          const slot = row[row.second as ServerSlotKey];
          return slot?.version || "—";
        },
      },
      {
        field: "secondHash",
        headerName: "Second Hash",
        minWidth: 120,
        renderCell: ({ row }) => {
          const slot = row[row.second as ServerSlotKey];
          return slot?.hash || "—";
        },
      },
      // Last column, and the only free-form one: a comment can be far longer
      // than any cell width, so the cell truncates and the full text (with
      // its line breaks preserved) is shown on hover.
      {
        field: "comment",
        headerName: "Comment",
        minWidth: 220,
        flex: 1,
        renderCell: ({ row }) =>
          row.comment ? (
            <Tooltip
              title={<Box sx={{ whiteSpace: "pre-wrap" }}>{row.comment}</Box>}
              placement="top"
            >
              <Box
                sx={{
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.comment}
              </Box>
            </Tooltip>
          ) : (
            "—"
          ),
      },
    ],
    []
  );

  // Stage opens `second` for testing — with no second slot there's nothing
  // to open, and deriveResult would publish a Beta entry with an empty url.
  const canStage =
    latest.state === OrcaDeployState.STABLE && !!latest[latest.second];

  const handleStageConfirm = async () => {
    setSubmitting(true);
    try {
      // Re-fetch right before writing: another operator may have appended a
      // row since this page loaded. Re-validate against what's actually
      // latest now instead of the possibly-stale `latest` from state.
      const freshLatest =
        (await fetchLatestOrcaDeployActivityLogRow(apiUrl)) ?? latest;
      const canStageFresh =
        freshLatest.state === OrcaDeployState.STABLE &&
        !!freshLatest[freshLatest.second];
      if (!canStageFresh) {
        window.alert(
          "Someone else changed the deploy state while this dialog was open. " +
            "Please review the latest state and try again."
        );
        setDialogMode(null);
        window.location.reload();
        return;
      }
      const next = stage(freshLatest);
      await dataProvider(apiUrl).create({
        resource: "orca-deploy-activity-logs",
        variables: {
          ...freshLatest,
          ...next,
          id: undefined,
          activityName: "Develop",
          activityOperatorName: getCurrentOperatorName(),
          // freshLatest carries the previous row's comment — blank it so a
          // note written on an earlier activity isn't re-attached to this one.
          comment: null,
          createdAt: Date.now(),
        },
      });
      window.location.reload();
    } catch (error: any) {
      window.alert(error?.message || "Failed to record stage activity");
      setDialogMode(null);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditConfirm = async () => {
    setSubmitting(true);
    try {
      // Edit has no state/pointer precondition, so just rebuild on top of
      // whatever is latest now rather than re-validating anything. Only
      // slots the user actually touched come from the form — everything
      // else comes from freshLatest, so an untouched slot can't clobber a
      // concurrent edit made to it while this dialog was open.
      const freshLatest =
        (await fetchLatestOrcaDeployActivityLogRow(apiUrl)) ?? latest;
      const nextSlots = SLOT_KEYS.reduce((acc, key) => {
        acc[key] = editedSlotKeys.has(key) ? editSlots[key] : freshLatest[key];
        return acc;
      }, {} as Record<ServerSlotKey, OrcaServerSlot | null>);
      // Second-pointer edits are only valid if the deploy is still in Stage
      // as of this fresh fetch — if someone Deployed/Rolled back while the
      // dialog was open, discard the pointer edit rather than repointing
      // second on top of a state transition that just happened. Prod is
      // never user-editable — it only moves via Deploy/Rollback.
      const secondChanged = editSecond !== latest.second;
      const applyPointerEdit =
        secondChanged && freshLatest.state === OrcaDeployState.STAGE;
      const next = editSlotsAndLinks(
        freshLatest,
        nextSlots,
        {
          prodLinkName: editProdLinkName,
          prodLinkDescription: editProdLinkDescription,
          fallbackLinkName: editFallbackLinkName,
          fallbackDescription: editFallbackDescription,
          betaLinkName: editBetaLinkName,
          betaLinkDescription: editBetaDescription,
        },
        applyPointerEdit
          ? { prod: freshLatest.prod, second: editSecond }
          : undefined
      );
      const activityParts = [
        ...Array.from(editedSlotKeys),
        ...(applyPointerEdit ? ["second pointer"] : []),
      ];
      await dataProvider(apiUrl).create({
        resource: "orca-deploy-activity-logs",
        variables: {
          ...freshLatest,
          ...next,
          id: undefined,
          activityName: `Edit (${activityParts.join(", ") || "links only"})`,
          activityOperatorName: getCurrentOperatorName(),
          comment: editComment.trim() || null,
          createdAt: Date.now(),
        },
      });
      window.location.reload();
    } catch (error: any) {
      window.alert(error?.message || "Failed to save edit");
      setDialogMode(null);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateNewConfirm = async () => {
    setSubmitting(true);
    try {
      const server1 = {
        url: createForm.server1Url,
        hash: createForm.server1Hash,
        version: createForm.server1Version,
      };
      // Leaving Second Url blank means there's no second environment yet —
      // store null, not an object with an empty url, so deriveResult treats
      // it the same as "no slot" instead of publishing a broken link.
      const server2 = createForm.server2Url
        ? {
            url: createForm.server2Url,
            hash: createForm.server2Hash,
            version: createForm.server2Version,
          }
        : null;
      const newRow = {
        server1,
        server2,
        server3: null,
        second: createForm.second,
        prod: createForm.prod,
        server4: null,
        state: OrcaDeployState.STABLE,
        prodLinkName: createForm.prodLinkName,
        prodLinkDescription: createForm.prodLinkDescription,
        fallbackLinkName: createForm.fallbackLinkName,
        fallbackDescription: createForm.fallbackDescription,
        betaLinkName: createForm.betaLinkName,
        betaLinkDescription: createForm.betaLinkDescription,
      };
      await dataProvider(apiUrl).create({
        resource: "orca-deploy-activity-logs",
        variables: {
          ...newRow,
          result: deriveResult(newRow),
          activityName: "Create New",
          activityOperatorName: getCurrentOperatorName(),
          comment: null,
          createdAt: Date.now(),
        },
      });
      window.location.reload();
    } catch (error: any) {
      window.alert(error?.message || "Failed to create new deploy record");
      setDialogMode(null);
    } finally {
      setSubmitting(false);
    }
  };

  // Warn when a touched slot is currently pointed at by prod or second —
  // that's the one live in ares.web right now, so editing it takes effect
  // immediately rather than sitting inert until a future Deploy/Rollback.
  const liveTouchedSlots = Array.from(editedSlotKeys).filter(
    (key) => key === latest.prod || key === latest.second
  );

  // Only slots with a url (from the form's current values, so filling in
  // server3's domain in this same dialog makes it selectable immediately —
  // no save-and-reopen round trip needed) can be pointed at by prod/second.
  // An empty slot would publish a broken link.
  const slotsWithUrl = SLOT_KEYS.filter((key) => !!editSlots[key].url);

  // Prod/second pointers only make sense to repoint while the deploy is
  // staged — Stable/Unstable both give `second` a specific meaning (live
  // fallback or beta-under-test) that Deploy/Rollback manage explicitly.
  const canEditPointers = latest.state === OrcaDeployState.STAGE;

  // Every touched slot's domain must be bare (no protocol/path) — toEntry
  // assembles it into "https://<domain>/web/sso?token=" unconditionally,
  // so anything else produces a broken or duplicated URL with no warning
  // at write time otherwise.
  const invalidTouchedSlots = Array.from(editedSlotKeys).filter(
    (key) => !isBareDomain(editSlots[key].url)
  );

  return (
    <List title="Orca Deploy Activity Logs">
      <OrcaDeployLifecycle state={latest.state} />

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Current Prod
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                {slotLabel(latest, latest.prod)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Current Second
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                {slotLabel(latest, latest.second)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              disabled={!canStage}
              onClick={() => setDialogMode("stage")}
            >
              Develop
            </Button>
            <Button
              variant="outlined"
              disabled={!hasAnyRecord}
              onClick={openEditDialog}
            >
              Edit
            </Button>
            {!hasAnyRecord && (
              <Button variant="outlined" onClick={() => setDialogMode("createNew")}>
                Create New
              </Button>
            )}
          </Box>
          {!canStage && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              {latest.state !== OrcaDeployState.STABLE
                ? `Develop is only available when the deploy is stable (current state: ${OrcaDeployStateLabels[latest.state]}).`
                : "Develop needs a Second slot to open — none is set. Use Create New to set one."}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        Operation Log
      </Typography>
      <DataGrid
        getRowId={(e) => e.id}
        {...dataGridProps}
        columns={columns}
        autoHeight
        paginationMode="server"
        rowsPerPageOptions={[10, 20, 50]}
        disableColumnFilter
      />

      {/* Develop confirm dialog */}
      <Dialog open={dialogMode === "stage"} onClose={() => setDialogMode(null)} maxWidth="md" fullWidth>
        <DialogTitle>Confirm: Develop</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Opens {slotLabel(latest, latest.second)} for a new staging cycle.
            Prod/Second pointers stay the same — only the state moves to stage.
          </Typography>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell>State</TableCell>
                <TableCell sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                  {OrcaDeployStateLabels[latest.state]}
                </TableCell>
                <TableCell align="center">→</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontWeight: 600, color: "warning.main" }}>
                  {OrcaDeployStateLabels[OrcaDeployState.STAGE]}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogMode(null)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleStageConfirm} variant="contained" disabled={submitting}>
            Confirm &amp; Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog: server slot url/hash/version + link names/descriptions */}
      <Dialog open={dialogMode === "edit"} onClose={() => setDialogMode(null)} maxWidth="md" fullWidth>
        <DialogTitle>Edit</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
            Second Pointer
          </Typography>
          <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
            <FormControl variant="standard" sx={{ width: "50%" }} disabled={!canEditPointers}>
              <InputLabel>Second</InputLabel>
              <Select value={editSecond} onChange={(e) => setEditSecond(e.target.value as ServerSlotKey)}>
                {slotsWithUrl.filter((key) => key !== latest.prod).map((key) => (
                  <MenuItem key={key} value={key}>
                    {key}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {!canEditPointers && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
              Second can only be repointed while the deploy is in Stage
              (current state: {OrcaDeployStateLabels[latest.state]}). Use
              Deploy/Rollback to change Prod.
            </Typography>
          )}

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            Server Slots
          </Typography>
          {SLOT_KEYS.map((key) => {
            const isLive = key === latest.prod || key === latest.second;
            const liveRole = key === latest.prod ? "prod" : "second";
            return (
              <Box key={key} sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 1 }}>
                  <Typography sx={{ width: "90px", fontWeight: 600 }}>
                    {key}
                    {isLive && (
                      <Typography component="span" variant="caption" color="error" sx={{ display: "block" }}>
                        live {liveRole}
                      </Typography>
                    )}
                  </Typography>
                  <TextField
                    label="Domain"
                    variant="standard"
                    value={editSlots[key].url}
                    onChange={(e) => setEditSlotField(key, "url", e.target.value)}
                    error={editedSlotKeys.has(key) && !isBareDomain(editSlots[key].url)}
                    helperText={
                      editedSlotKeys.has(key) && !isBareDomain(editSlots[key].url)
                        ? "Bare domain only — no https://, path, or query"
                        : undefined
                    }
                    sx={{ width: "45%" }}
                  />
                  <TextField
                    label="Version (optional)"
                    variant="standard"
                    value={editSlots[key].version}
                    onChange={(e) => setEditSlotField(key, "version", e.target.value)}
                    sx={{ width: "25%" }}
                  />
                  <TextField
                    label="Hash (optional)"
                    variant="standard"
                    value={editSlots[key].hash}
                    onChange={(e) => setEditSlotField(key, "hash", e.target.value)}
                    sx={{ width: "25%" }}
                  />
                </Box>
              </Box>
            );
          })}
          {liveTouchedSlots.length > 0 && (
            <Typography
              variant="body2"
              sx={{ mt: 1, mb: 2, p: 1.5, borderRadius: 1, bgcolor: "error.light", color: "error.contrastText" }}
            >
              {liveTouchedSlots.join(", ")} {liveTouchedSlots.length > 1 ? "are" : "is"} currently
              live (prod/second) — this change takes effect immediately for
              users, not just on the next Deploy/Rollback.
            </Typography>
          )}

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            Link Names
          </Typography>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              label="Prod Link Name"
              variant="standard"
              value={editProdLinkName}
              onChange={(e) => setEditProdLinkName(e.target.value)}
              sx={{ width: "50%" }}
            />
            <TextField
              label="Prod Link Description"
              variant="standard"
              value={editProdLinkDescription}
              onChange={(e) => setEditProdLinkDescription(e.target.value)}
              sx={{ width: "50%" }}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              label="Beta Link Name"
              variant="standard"
              value={editBetaLinkName}
              onChange={(e) => setEditBetaLinkName(e.target.value)}
              sx={{ width: "50%" }}
            />
            <TextField
              label="Beta Link Description"
              variant="standard"
              value={editBetaDescription}
              onChange={(e) => setEditBetaDescription(e.target.value)}
              sx={{ width: "50%" }}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              label="Fallback Link Name"
              variant="standard"
              value={editFallbackLinkName}
              onChange={(e) => setEditFallbackLinkName(e.target.value)}
              sx={{ width: "50%" }}
            />
            <TextField
              label="Fallback Link Description"
              variant="standard"
              value={editFallbackDescription}
              onChange={(e) => setEditFallbackDescription(e.target.value)}
              sx={{ width: "50%" }}
            />
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            Comment
          </Typography>
          <TextField
            label="Comment (optional)"
            variant="standard"
            value={editComment}
            onChange={(e) => setEditComment(e.target.value)}
            multiline
            minRows={2}
            placeholder="Why this edit was made — shown in the Operation Log"
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogMode(null)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleEditConfirm}
            variant="contained"
            color={liveTouchedSlots.length > 0 ? "error" : "primary"}
            disabled={submitting || invalidTouchedSlots.length > 0}
          >
            Confirm &amp; Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create New dialog */}
      <Dialog open={dialogMode === "createNew"} onClose={() => setDialogMode(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Deploy Record</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Starts a brand-new deploy record from scratch (e.g. bootstrapping a
            fresh environment pair). This does not carry forward any field from
            the current latest row.
          </Typography>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              label="Prod Domain"
              variant="standard"
              value={createForm.server1Url}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, server1Url: e.target.value }))
              }
              error={!!createForm.server1Url && !isBareDomain(createForm.server1Url)}
              helperText={
                !!createForm.server1Url && !isBareDomain(createForm.server1Url)
                  ? "Bare domain only — no https://, path, or query"
                  : undefined
              }
              sx={{ width: "34%" }}
            />
            <TextField
              label="Prod Hash (optional)"
              variant="standard"
              value={createForm.server1Hash}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, server1Hash: e.target.value }))
              }
              sx={{ width: "33%" }}
            />
            <TextField
              label="Prod Version (optional)"
              variant="standard"
              value={createForm.server1Version}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, server1Version: e.target.value }))
              }
              sx={{ width: "33%" }}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              label="Second Domain"
              variant="standard"
              value={createForm.server2Url}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, server2Url: e.target.value }))
              }
              error={!!createForm.server2Url && !isBareDomain(createForm.server2Url)}
              helperText={
                !!createForm.server2Url && !isBareDomain(createForm.server2Url)
                  ? "Bare domain only — no https://, path, or query"
                  : undefined
              }
              sx={{ width: "34%" }}
            />
            <TextField
              label="Second Hash (optional)"
              variant="standard"
              value={createForm.server2Hash}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, server2Hash: e.target.value }))
              }
              sx={{ width: "33%" }}
            />
            <TextField
              label="Second Version (optional)"
              variant="standard"
              value={createForm.server2Version}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, server2Version: e.target.value }))
              }
              sx={{ width: "33%" }}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              label="Prod Link Name"
              variant="standard"
              value={createForm.prodLinkName}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, prodLinkName: e.target.value }))
              }
              sx={{ width: "50%" }}
            />
            <TextField
              label="Beta Link Name"
              variant="standard"
              value={createForm.betaLinkName}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, betaLinkName: e.target.value }))
              }
              sx={{ width: "50%" }}
            />
          </Box>
          <Typography
            variant="body2"
            sx={{ p: 1.5, borderRadius: 1, bgcolor: "warning.light", color: "warning.contrastText" }}
          >
            New record will start in the stable state with server1 as prod and
            server2 as second.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogMode(null)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateNewConfirm}
            variant="contained"
            disabled={
              submitting ||
              !isBareDomain(createForm.server1Url) ||
              (!!createForm.server2Url && !isBareDomain(createForm.server2Url))
            }
          >
            Confirm &amp; Create
          </Button>
        </DialogActions>
      </Dialog>
    </List>
  );
};
